'use client'

/**
 * A use-case recommendation under a Home answer (#16826, PRD R7).
 *
 * When a question reveals a job a SchoolAI feature does, the turn may carry
 * ONE recommendation (`@/lib/ai-chat/feature-recommendations`): the
 * feature's name, one sentence on how it applies, and two buttons.
 *
 *  - **Start** on an href recommendation is a real link. Following it
 *    records the start and leaves at the document level, after the record
 *    has had a moment to land — the same way Connect now leaves
 *    (`leaveAfterReports`, `./next-steps`). A modified click (new tab) is
 *    left to the browser and still records.
 *  - **Start** on a draft recommendation records the start and opens the
 *    foundation's {@link DraftCard} (#16813) with the drafted pulse request
 *    or Priority. Its Approve reaches `approveFeatureRecommendationDraft`,
 *    and its Not now only closes it: a started recommendation is never also
 *    dismissed. Pressing Start again toggles the card, and closing returns
 *    focus to Start. Once started, the draft card stays mounted and is only
 *    hidden while closed, so reopening it shows the asker's edits — or, after
 *    Approve, the approved state and its link — never a fresh draft. A
 *    successful approve is also kept here, and answered again rather than
 *    sent twice.
 *  - **Not now** records the dismissal (fire-and-forget) and removes the
 *    card. The server will not offer it again in this conversation.
 *
 * Every string is from the closed map, rendered as plain text. Semantic
 * tokens only; keyboard-operable native controls; results announced in one
 * `role="status"` region.
 */

import { type MouseEvent, useId, useRef, useState } from 'react'

import {
  approveFeatureRecommendationDraft,
  dismissFeatureRecommendation,
  startFeatureRecommendation,
} from '@/app/actions/feature-recommendations'
import { ArrowRight, Lightbulb, PenLine } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button, buttonClassName } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { ShownFeatureRecommendation } from '@/lib/ai-chat/feature-recommendations'
import type { Draft, NextStep } from '@/lib/ai-chat/next-steps'

import { type DraftApproveResult, DraftCard } from './draft-card'
import { leaveAfterReports, opensElsewhere } from './next-steps'

type OffRampStep = Extract<NextStep, { kind: 'offramp' }>

/** The badge: where the suggestion comes from. */
const RECOMMENDATION_SOURCE = 'SchoolAI'

/** Said when a Start could not be recorded and nothing was opened. */
const START_FAILED = 'Could not start that. Try again.'

/**
 * The draft card's step for a draft start. The type is the off-ramp the
 * same draft kind belongs to, so the card's copy (an Ask is "saved, never
 * sent") is the one that kind already uses.
 */
function draftStep(
  recommendation: ShownFeatureRecommendation,
  draft: Draft,
): OffRampStep {
  return {
    kind: 'offramp',
    id: `recommendation:${recommendation.id}`,
    type: draft.kind === 'pulse' ? 'ask' : 'plan',
    label: recommendation.startLabel,
    source: RECOMMENDATION_SOURCE,
    draft,
  }
}

export function RecommendationCard({
  recommendation,
}: {
  recommendation: ShownFeatureRecommendation
}) {
  const headingId = useId()
  const [dismissed, setDismissed] = useState(false)
  const [started, setStarted] = useState(false)
  const [open, setOpen] = useState(false)
  const [starting, setStarting] = useState(false)
  const [status, setStatus] = useState('')
  const startButton = useRef<HTMLButtonElement>(null)
  const approved = useRef<DraftApproveResult | null>(null)

  const { recordId, start } = recommendation

  if (dismissed) return null

  function recordStart() {
    return startFeatureRecommendation({ recordId })
  }

  function followLink(event: MouseEvent<HTMLAnchorElement>, href: string) {
    setStarted(true)
    const reported = recordStart().catch(() => {
      // The navigation is the asker's outcome; a lost record must not stop it.
    })
    if (opensElsewhere(event)) return
    event.preventDefault()
    void leaveAfterReports(href, reported)
  }

  async function startDraft() {
    if (open) {
      setOpen(false)
      return
    }
    if (started) {
      setOpen(true)
      return
    }
    setStarting(true)
    setStatus('')
    try {
      const result = await recordStart()
      if (!result.success) {
        setStatus(result.error || START_FAILED)
        return
      }
      setStarted(true)
      setOpen(true)
    } catch {
      // A rejected action must not strand the button — see sub-issue R carve-out
      setStatus(START_FAILED)
    } finally {
      setStarting(false)
    }
  }

  function notNow() {
    dismissFeatureRecommendation({ recordId }).catch(() => {
      // Recording the dismissal is not worth blocking it.
    })
    setDismissed(true)
  }

  async function approveDraft(draft: Draft) {
    if (approved.current) return approved.current
    const result = await approveFeatureRecommendationDraft({ recordId, draft })
    if (result.success) approved.current = result
    return result
  }

  function closeDraft() {
    setOpen(false)
    startButton.current?.focus()
  }

  return (
    <Card
      data-testid="ai-chat-recommendation"
      role="group"
      aria-labelledby={headingId}
      className="mt-3 border-stroke-extra-light bg-surface-panel p-3 shadow-none"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Lightbulb
          className="h-4 w-4 shrink-0 text-text-secondary"
          aria-hidden
        />
        <h4 id={headingId} className="text-sm font-semibold text-text-primary">
          {`Try ${recommendation.name}`}
        </h4>
        <Badge variant="outline" className="text-xs font-medium">
          {RECOMMENDATION_SOURCE}
        </Badge>
      </div>
      <p className="mt-1.5 text-sm text-text-secondary">{recommendation.how}</p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {start.kind === 'href' ? (
          <a
            href={start.href}
            onClick={event => followLink(event, start.href)}
            className={buttonClassName({
              size: 'sm',
              className: 'pointer-coarse:min-h-11',
            })}
          >
            {recommendation.startLabel}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        ) : (
          <Button
            ref={startButton}
            type="button"
            size="sm"
            className="pointer-coarse:min-h-11"
            disabled={starting}
            aria-expanded={open}
            onClick={startDraft}
          >
            <PenLine className="h-4 w-4" aria-hidden />
            {recommendation.startLabel}
          </Button>
        )}
        {!started && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="pointer-coarse:min-h-11"
            disabled={starting}
            onClick={notNow}
          >
            Not now
          </Button>
        )}
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-text-secondary"
        >
          {status}
        </p>
      </div>

      {started && start.kind === 'draft' && (
        <div hidden={!open}>
          <DraftCard
            step={draftStep(recommendation, start.draft)}
            unmetNeedId={null}
            onClose={closeDraft}
            onApprove={draft => approveDraft(draft ?? start.draft)}
            onDismiss={() => {
              // Started, so never also dismissed: closing is all Not now does.
            }}
          />
        </div>
      )}
    </Card>
  )
}
